import { NoSsr } from '@material-ui/core';
import { darken, makeStyles } from '@material-ui/core/styles';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import clsx from 'clsx';
import React from 'react';
import { useRecoilValue } from 'recoil';
import { isAuthenticatedState, showPricesState } from '../../../api/appState';
import { CatalogueProduct } from '../../../api/products/catalogue';
import { activeFavoriteProducts, showStockAvailability } from '../../../config';
import { isMasterProduct } from '../../../helpers/isMasterProduct';
import { isProductCanceled } from '../../../helpers/isProductCanceled';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';
import ProductDetailFavoriteToggle from '../../ProductDetailPage/components/ProductDetailFavoriteToggle';
import ProductDetailAlternatives from '../../ProductDetailPage/components/ProductDetailAlternatives';
import CataloguePageListContentItemRowAddToCart from './CataloguePageListContentItemRowAddToCart';
import CataloguePageListContentItemRowImage from './CataloguePageListContentItemRowImage';
import CataloguePageListContentItemRowInlineParameters from './CataloguePageListContentItemRowInlineParameters';
import CataloguePageListContentItemRowName from './CataloguePageListContentItemRowName';
import CataloguePageListContentItemRowPrice from './CataloguePageListContentItemRowPrice';
import CataloguePageListContentItemRowStockInfo from './CataloguePageListContentItemRowStockInfo';
import CataloguePageListContentItemWarning from './CataloguePageListContentItemWarning';

export interface CataloguePageListContentItemRowProps
  extends StandardDivProps<CataloguePageListContentItemRowClassKey> {
  product: CatalogueProduct;
}

export type CataloguePageListContentItemRowClassKey = 'root' | 'warning';

const themeSettings = { name: 'CataloguePageListContentItemRow' };

const useStyles = makeStyles(
  (theme) => ({
    root: {
      width: '100%',
      padding: theme.spacing(1),
      marginBottom: theme.spacing(1),
      borderBottom: `2px solid ${darken(theme.palette.divider, 0.4)}`,
      position: 'relative',

      // GRID
      display: 'grid',
      gridColumnGap: 0, // theme.spacing(1),
      gridRowGap: 0, // theme.spacing(1),
      justifyItems: 'stretch',
      alignItems: 'stretch',
      justifyContent: 'stretch',
      gridTemplateRows: 'max-content',

      gridTemplateColumns: '96px 1fr 140px',
      gridTemplateAreas: `
        "image    name        addToCart"
        "image    stockInfo   addToCart"
        "image    price       addToCart"
        "image    parameters  addToCart"
        "variants    variants    variants"
      `,
      '&:hover $favoriteToggle': {
        opacity: 1,
      },
      [theme.breakpoints.down(500)]: {
        gridTemplateColumns: '96px 1fr',
        justifyItems: 'stretch',
        gridTemplateAreas: `
        "image    name"
        "image    stockInfo"
        "image    price"
        "image    parameters"
        "addToCart addToCart"
        "variants    variants"
      `,
      },

      [theme.breakpoints.up('lg')]: {
        gridTemplateAreas: `
        "image    name        addToCart"
        "image    stockInfo   addToCart"
        "image    price       addToCart"
        "image    parameters  addToCart"
        "image    variants    variants"
      `,
      },

      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-columns': '96px auto auto',
      },
    },
    image: {
      gridArea: 'image',
      width: theme.spacing(12),
      maxHeight: theme.spacing(13.5),
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),

      // Fixes grid in IE 10+
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 1,
        '-ms-grid-column': 1,
        '-ms-grid-row-span': 2,
      },
    },
    name: {
      gridArea: 'name',
      borderBottom: 'none',
      // Fixes grid in IE 10+
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 1,
        '-ms-grid-column': 1,
        // '-ms-grid-column-span': 2,
      },
    },
    parameters: {
      // Fix grid in IE 10+
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 2,
        '-ms-grid-column': 2,
      },
      [theme.breakpoints.down('xs')]: {
        marginTop: theme.spacing(2),
      },
    },
    price: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      paddingBottom: theme.spacing(1),
      // '& > div': {
      //   display: 'block',
      //   textAlign: 'right',
      //   paddingRight: theme.spacing(1),
      // },

      // Fix grid in IE 10+
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 1,
        '-ms-grid-column': 4,
      },
    },
    addToCart: {
      gridArea: 'addToCart',

      // Fix grid in IE 10+
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 1,
        '-ms-grid-column': 5,
        '-ms-grid-row-span': 2,
      },
    },
    stockInfo: {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'flex-end',
      [theme.breakpoints.down('xs')]: {
        paddingTop: theme.spacing(2),
      },
      // Fix grid in IE 10+
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 2,
        '-ms-grid-column': 3,
      },
    },
    variants: {
      gridArea: 'variants',
      [theme.breakpoints.down('xs')]: {
        borderBottom: 'none',
      },
      // Fix grid in IE 10+
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 2,
        '-ms-grid-column': 2,
      },
    },
    packageInfo: {
      gridArea: 'packageInfo',
      display: 'flex',
      alignItems: 'flex-start',
      paddingLeft: 20,
      justifyContent: 'flex-center',
      [theme.breakpoints.down('xs')]: {
        borderBottom: 'none',
      },
      // Fix grid in IE 10+
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 2,
        '-ms-grid-column': 2,
      },
    },
    warning: {
      gridArea: 'warning',
      paddingLeft: '12px',
    },
    favoriteToggle: {
      position: 'absolute',
      top: theme.spacing(1),
      right: theme.spacing(1),
      zIndex: 1,
      transition: 'opacity 0.3s ease',
    },
  }),
  themeSettings,
);

const CataloguePageListContentItemRow = React.forwardRef<HTMLDivElement, CataloguePageListContentItemRowProps>(
  ({ className, classes: pClasses, product }, ref) => {
    const t = useTranslateWithFallback();
    const classes = useStyles({ classes: pClasses });
    // const customerRegion = useRecoilValue(customerRegionState);
    const isAuthenticated = useRecoilValue(isAuthenticatedState);
    const showPrices = useRecoilValue(showPricesState);

    const productIsCanceled = isProductCanceled(product.taxonomies);
    const isNotMasterProduct = !isMasterProduct(product.taxonomies);

    return (
      <div ref={ref} className={clsx(classes.root, className)}>
        <CataloguePageListContentItemRowImage className={classes.image} product={product} />
        <CataloguePageListContentItemRowName className={classes.name} product={product} />
        <CataloguePageListContentItemRowInlineParameters className={classes.parameters} product={product} />
        {activeFavoriteProducts && isAuthenticated && isNotMasterProduct && (
          <NoSsr>
            <ProductDetailFavoriteToggle className={classes.favoriteToggle} productId={product.id} />
          </NoSsr>
        )}
        {/* {isAuthenticated && (
          <TextFieldPreview
            label={t('cataloguePage/item/package')}
            value={(customerRegion === 'SK' ? product.extra?.packSizeTextSK : product.extra?.packSizeTextCZ) || ''}
            size='small'
            align='left'
            alignLabel
            className={classes.packageInfo}
          />
        )} */}
        {productIsCanceled === false ? (
          <>
            <CataloguePageListContentItemWarning className={classes.variants} product={product} type='detail' />
            {isAuthenticated && isNotMasterProduct && (
              <>
                {showStockAvailability && isAuthenticated && (
                  <CataloguePageListContentItemRowStockInfo className={classes.stockInfo} product={product} />
                )}
                {isAuthenticated && showPrices && (
                  <CataloguePageListContentItemRowPrice className={classes.price} product={product} />
                )}
                <CataloguePageListContentItemRowAddToCart className={classes.addToCart} product={product} />
              </>
            )}
          </>
        ) : (
          <ProductDetailAlternatives
            className={classes.warning}
            sectionName={t('productDetailPage/replacements')}
            productAlternatives={product.replacements}
          />
        )}
      </div>
    );
  },
);

CataloguePageListContentItemRow.displayName = 'CataloguePageListContentItemRow';

export default withThemeProps(themeSettings)(CataloguePageListContentItemRow);
